import React from "react";
import { Box, Paper, Typography, Grid, IconButton } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { LeadStatusRenderer } from "../LeadStatusRenderer/LeadStatusRenderer";
import { CustomChip } from "../CustomChip";
import { useTimezoneFormatter } from "../../hooks/useTimezoneFormatter";

export interface IEventCardData {
  title: string;
  status?: string;
  venue?: string;
  eventDate?: string;
  bookingDate?: string;
  guests?: number;
  rooms?: number;
  followupDate?: string;
  paymentStatus?: string;
  gstPanNumber?: string;
  contact?: string;
  [key: string]: any;
}

interface IEventCardProps {
  data: IEventCardData;
  index: number;
  expanded?: boolean;
  onClick: (index: number) => void;
  renderActions?: (data: IEventCardData, index: number) => React.ReactNode;
  renderCustomFields?: (data: IEventCardData) => React.ReactNode;
}

export const EventCard: React.FC<IEventCardProps> = ({
  data,
  index,
  expanded = false,
  onClick,
  renderActions,
  renderCustomFields,
}) => {
  const { ftDate } = useTimezoneFormatter();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        cursor: "pointer",
        border: "1px solid",
        borderColor: expanded ? "primary.main" : "divider",
        borderLeft: "3px solid",
        borderLeftColor: "primary.main",
        backgroundColor: expanded ? "action.hover" : "background.paper",
        transition: "border-color 0.2s, box-shadow 0.2s",
        "&:hover": {
          boxShadow: 2,
          borderColor: "primary.main",
          borderLeftColor: "primary.main",
        },
      }}
      onClick={() => onClick(index)}
    >
      {/* Header row: Title + Status + Accordion icon */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            color="primary.main"
            noWrap
          >
            {data.title || "--"}
          </Typography>
          {data.status && (
            <LeadStatusRenderer status={data.status || "ONGOING"} />
          )}
        </Box>

        {/* Accordion icon */}
        <IconButton size="small" sx={{ ml: 0.5, p: 0.25 }}>
          {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Compact info grid - 2 columns */}
      <Grid container spacing={1} sx={{ mt: 0.5 }}>
        {data.venue && (
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                mb: 0.25,
              }}
            >
              Venue
            </Typography>
            <Typography variant="body2" fontWeight={600} noWrap>
              {data.venue}
            </Typography>
          </Grid>
        )}
        {data.eventDate && (
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                mb: 0.25,
              }}
            >
              Event Date
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {data.eventDate ? ftDate(data.eventDate) : "--"}
            </Typography>
          </Grid>
        )}
        {data.guests !== undefined && data.guests > 0 && (
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                mb: 0.25,
              }}
            >
              Guests
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {data.guests}
            </Typography>
          </Grid>
        )}
        {data.paymentStatus && (
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                mb: 0.25,
              }}
            >
              Payment
            </Typography>
            <CustomChip
              color={
                data.paymentStatus === "PAID"
                  ? "success"
                  : data.paymentStatus === "PARTIALLY_PAID"
                    ? "warning"
                    : "error"
              }
              content={data.paymentStatus || "UNPAID"}
              size="small"
            />
          </Grid>
        )}
        {data.contact && (
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                mb: 0.25,
              }}
            >
              Contact
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {data.contact}
            </Typography>
          </Grid>
        )}
        {data.bookingDate && (
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                mb: 0.25,
              }}
            >
              Booking Date
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {data.bookingDate ? ftDate(data.bookingDate) : "--"}
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Action Buttons */}
      {renderActions && (
        <Box
          sx={{ display: "flex", gap: 0.5, mt: 0.5 }}
          onClick={(e) => e.stopPropagation()}
        >
          {renderActions(data, index)}
        </Box>
      )}

      {/* Custom Fields */}
      {renderCustomFields && renderCustomFields(data)}
    </Paper>
  );
};
